import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { SlidersHorizontal, History, Plus, Trash2 } from 'lucide-react';
import { api, extractError } from '../../lib/api';
import { useConfirm } from '../../lib/confirm';
import { fmtDateTime } from '../../lib/date';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { fmtDays } from './constants';

export default function AbsenceBalances({ costCenters, canEdit, canDelete, onOpenDetail, reloadKey }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [type, setType] = useState('Urlaub');
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [adjustFor, setAdjustFor] = useState(null);

  const kstById = useMemo(() => Object.fromEntries(costCenters.map(k => [k.id, k])), [costCenters]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/absence-balances', { params: { year, type } });
      setData(r.data);
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, type, reloadKey]);

  const isVacation = type === 'Urlaub';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-44 h-8 text-sm font-semibold" data-testid="balance-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Urlaub">Urlaub</SelectItem>
            <SelectItem value="Krankheit">Krankheit</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-24 h-8 text-sm font-semibold" data-testid="balance-year"><SelectValue /></SelectTrigger>
          <SelectContent>{[year - 2, year - 1, year, year + 1].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <div className="text-xs text-slate-500">Zyklus = Kalenderjahr {year}. Saldo = Anspruch + Resturlaub + Korrekturen − genommen − gebucht.</div>
      </div>

      <div className="border rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            {isVacation ? (
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Richtlinienzuordnung</TableHead>
                <TableHead className="text-right">Aktueller Saldo</TableHead>
                <TableHead className="text-right">In diesem Zyklus genommen</TableHead>
                <TableHead className="text-right">Anspruch in diesem Zyklus</TableHead>
                <TableHead className="text-right">Resturlaub (Vorjahr)</TableHead>
                <TableHead className="text-right">Vor Ende des Zyklus gebucht</TableHead>
                <TableHead className="text-right">Korrekturen</TableHead>
                <TableHead className="text-right">Aktion</TableHead>
              </TableRow>
            ) : (
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Kostenstelle</TableHead>
                <TableHead className="text-right">Krankheitstage</TableHead>
                <TableHead className="text-right">Fälle</TableHead>
              </TableRow>
            )}
          </TableHeader>
          <TableBody>
            {data.items.map((it) => {
              const kst = kstById[it.cost_center_id];
              if (isVacation) {
                return (
                  <TableRow key={it.employee_id} data-testid={`balance-row-${it.employee_id}`}>
                    <TableCell>
                      <button className="text-left" onClick={() => onOpenDetail?.(it.employee_id, kst?.name, year)} data-testid={`balance-name-${it.employee_id}`}>
                        <div className="font-semibold text-slate-800 hover:text-sky-700">{it.employee_name}</div>
                        <div className="text-[11px] text-slate-500">{kst?.name || '—'}</div>
                      </button>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{it.policy_name || <span className="text-slate-400">Keine Richtlinie</span>}</TableCell>
                    <TableCell className="text-right font-mono font-bold">{fmtDays(it.saldo)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtDays(it.taken)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtDays(it.entitlement)}{it.is_override && <Badge variant="outline" className="ml-1 bg-sky-50 text-sky-700 border-sky-200 text-[10px]">manuell</Badge>}</TableCell>
                    <TableCell className="text-right font-mono">{it.carryover ? <span className="text-emerald-700">+{fmtDays(it.carryover)}</span> : <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-right font-mono">{fmtDays(it.booked)}</TableCell>
                    <TableCell className="text-right font-mono text-slate-500">{it.adjustments ? (it.adjustments > 0 ? '+' : '') + fmtDays(it.adjustments) : '—'}</TableCell>
                    <TableCell className="text-right">
                      {canEdit && <Button size="sm" variant="ghost" onClick={() => setAdjustFor(it)} title="Urlaub korrigieren" data-testid={`balance-adjust-${it.employee_id}`}><SlidersHorizontal size={14} /></Button>}
                    </TableCell>
                  </TableRow>
                );
              }
              return (
                <TableRow key={it.employee_id} data-testid={`balance-row-${it.employee_id}`}>
                  <TableCell>
                    <button className="text-left font-semibold text-slate-800 hover:text-sky-700" onClick={() => onOpenDetail?.(it.employee_id, kst?.name, year)} data-testid={`balance-name-${it.employee_id}`}>{it.employee_name}</button>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{kst?.name || '—'}</TableCell>
                  <TableCell className="text-right font-mono font-bold">{fmtDays(it.sick_days)}</TableCell>
                  <TableCell className="text-right font-mono">{it.cases}</TableCell>
                </TableRow>
              );
            })}
            {!loading && data.items.length === 0 && <TableRow><TableCell colSpan={isVacation ? 9 : 4} className="text-center text-slate-500 py-8">Keine Daten.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <AdjustmentDialog open={!!adjustFor} onOpenChange={(v) => !v && setAdjustFor(null)} item={adjustFor} year={year} canDelete={canDelete} onChanged={load} />
    </div>
  );
}

function AdjustmentDialog({ open, onOpenChange, item, year, canDelete, onChanged }) {
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const { confirm } = useConfirm();

  const loadHistory = async () => {
    if (!item) return;
    try { const r = await api.get('/absence-balance-adjustments', { params: { employee_id: item.employee_id, year } }); setHistory(r.data); }
    catch { setHistory([]); }
  };
  useEffect(() => { if (open) { setDelta(''); setReason(''); loadHistory(); } /* eslint-disable-next-line */ }, [open, item]);

  const submit = async () => {
    const d = parseFloat(String(delta).replace(',', '.'));
    if (Number.isNaN(d) || d === 0) { toast.error('Bitte einen Korrekturwert (± Tage) angeben.'); return; }
    if (!reason.trim()) { toast.error('Ein Grund ist erforderlich.'); return; }
    setLoading(true);
    try {
      await api.post('/absence-balance-adjustments', { employee_id: item.employee_id, year, delta_days: d, reason: reason.trim() });
      toast.success('Korrektur gespeichert.');
      setDelta(''); setReason('');
      await loadHistory();
      onChanged?.();
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!(await confirm({ title: 'Korrektur löschen?', variant: 'destructive', confirmLabel: 'Löschen' }))) return;
    try { await api.delete(`/absence-balance-adjustments/${id}`); await loadHistory(); onChanged?.(); toast.success('Korrektur gelöscht.'); }
    catch (err) { toast.error(extractError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="adjustment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><SlidersHorizontal size={18} />Urlaub korrigieren</DialogTitle>
          <DialogDescription>{item?.employee_name} · Zyklus {year}. Positive Werte erhöhen, negative reduzieren den Anspruch.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Korrektur (± Tage)</Label><Input value={delta} onChange={e => setDelta(e.target.value)} placeholder="z.B. -2 oder 1.5" data-testid="adjust-delta" /></div>
            <div className="col-span-2"><Label className="text-xs">Grund</Label><Input value={reason} onChange={e => setReason(e.target.value)} placeholder="z.B. Resturlaub Vorjahr" data-testid="adjust-reason" /></div>
          </div>
          <Button size="sm" onClick={submit} disabled={loading} data-testid="adjust-submit"><Plus size={14} className="mr-1" />Korrektur hinzufügen</Button>

          <div className="pt-1">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 mb-1"><History size={13} />Historie</div>
            <div className="border rounded-md divide-y max-h-48 overflow-y-auto">
              {history.length === 0 && <div className="p-3 text-sm text-slate-400">Noch keine Korrekturen.</div>}
              {history.map(h => (
                <div key={h.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`adjust-history-${h.id}`}>
                  <div>
                    <span className={`font-mono font-bold ${h.delta_days > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{h.delta_days > 0 ? '+' : ''}{fmtDays(h.delta_days)} T</span>
                    <span className="text-slate-700 ml-2">{h.reason}</span>
                    <div className="text-[11px] text-slate-400">{h.by_user_name || '—'} · {fmtDateTime(h.created_at)}</div>
                  </div>
                  {canDelete && <Button size="icon" variant="ghost" className="text-rose-600 h-7 w-7" onClick={() => del(h.id)}><Trash2 size={13} /></Button>}
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Schließen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
